import { useState } from "react";
import {
  makeEconomy,
  fuelLogsForCar,
  type CanonicalStore,
  type Car,
} from "../../core/index.ts";
import { SplitFlapNumber } from "../components/SplitFlapNumber.tsx";
import { CarForm } from "../forms/CarForm.tsx";
import { TankCalcForm } from "../forms/TankCalcForm.tsx";
import { ValuationCard, ValuationView } from "./ValuationView.tsx";
import { MaintenanceView } from "./MaintenanceView.tsx";
import { ServicesView } from "./ServicesView.tsx";
import { ExportView } from "./ExportView.tsx";
import { round0 } from "../format.ts";

interface Props {
  store: CanonicalStore;
  selectedId: string;
  onSelect: (id: string) => void;
  save: (s: CanonicalStore) => void;
  isPro: boolean;
  requirePro: () => void;
  onOpenTrips: () => void;
  onOpenTyres: () => void;
}

function StatTile({ k, value, unit, color = "var(--cream)" }: { k: string; value: string; unit?: string; color?: string }) {
  return (
    <div className="stattile">
      <div className="k">{k}</div>
      <SplitFlapNumber value={value} size={22} weight={500} color={color} />
      {unit ? <div className="u">{unit}</div> : <div style={{ height: 12 }} />}
    </div>
  );
}

export function GarageView({ store, selectedId, onSelect, save, isPro, requirePro, onOpenTrips, onOpenTyres }: Props) {
  const metric = store.profile.unitSystem === "metric";
  const economy = makeEconomy(metric);
  const currency = store.profile.displayCurrency;
  const car: Car | undefined = store.cars.find((c) => c.id === selectedId) ?? store.cars[0];
  const [form, setForm] = useState<"editCar" | "addCar" | "tank" | null>(null);
  const [sub, setSub] = useState<"valuation" | "maintenance" | "services" | "export" | null>(null);
  const guard = (fn: () => void) => (isPro ? fn() : requirePro());

  if (car && sub === "valuation") return <ValuationView store={store} car={car} currency={currency} metric={metric} isPro={isPro} requirePro={requirePro} onBack={() => setSub(null)} />;
  if (car && sub === "maintenance") return <MaintenanceView store={store} car={car} save={save} isPro={isPro} requirePro={requirePro} onBack={() => setSub(null)} />;
  if (car && sub === "services") return <ServicesView store={store} save={save} onBack={() => setSub(null)} />;
  if (car && sub === "export") return <ExportView store={store} car={car} isPro={isPro} requirePro={requirePro} onBack={() => setSub(null)} />;

  return (
    <div className="container glow">
      <div className="eyebrow amber" style={{ paddingTop: 8 }}>GARAGE · BAY 01</div>
      <h1 className="headline">THE <span className="accent">garage.</span></h1>

      <div className="hscroll" role="tablist" aria-label="Vehicles">
        {store.cars.map((c, i) => {
          const locked = !isPro && i > 0; // free tier: one car only
          return (
            <button
              key={c.id}
              className={`chip${c.id === (car?.id ?? "") ? " active" : ""}`}
              style={locked ? { opacity: 0.55 } : undefined}
              onClick={() => (locked ? requirePro() : onSelect(c.id))}
            >
              {locked ? "🔒 " : ""}{`${c.make ?? ""} ${c.model ?? ""}`.trim().toUpperCase() || "CAR"}
            </button>
          );
        })}
        <button className="chip" onClick={() => guard(() => setForm("addCar"))}>+ ADD NEW CAR</button>
      </div>

      {car && <CarBody store={store} car={car} economy={economy} currency={currency} metric={metric} />}

      {car && (
        <ValuationCard store={store} car={car} currency={currency} isPro={isPro} onOpen={() => guard(() => setSub("valuation"))} />
      )}

      {car && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
          <button className="btn ghost" style={{ fontSize: 11, width: "100%", textAlign: "left" }} onClick={() => guard(() => setSub("maintenance"))}>🔔 MAINTENANCE REMINDERS{isPro ? "" : " (PRO)"}</button>
          <button className="btn ghost" style={{ fontSize: 11, width: "100%", textAlign: "left" }} onClick={() => setSub("services")}>🔧 PREFERRED SERVICES</button>
          <button className="btn ghost" style={{ fontSize: 11, width: "100%", textAlign: "left" }} onClick={() => guard(() => setSub("export"))}>⬇ EXPORT CAR DATA{isPro ? "" : " (PRO)"}</button>
        </div>
      )}

      {car && (
        <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
          <button className="btn ghost" style={{ fontSize: 11 }} onClick={() => setForm("tank")}>{(car.fuelType ?? "").toLowerCase() === "electric" ? "BATTERY" : "TANK"} CALCULATOR</button>
          <button className="btn ghost" style={{ fontSize: 11 }} onClick={() => guard(onOpenTrips)}>TRIPS{isPro ? "" : " (PRO)"}</button>
          <button className="btn ghost" style={{ fontSize: 11 }} onClick={() => guard(onOpenTyres)}>TIRES{isPro ? "" : " (PRO)"}</button>
          <button className="btn ghost" style={{ fontSize: 11 }} onClick={() => setForm("editCar")}>EDIT CAR</button>
        </div>
      )}

      {form === "addCar" && <CarForm store={store} onSave={save} onClose={() => setForm(null)} />}
      {form === "editCar" && car && <CarForm store={store} editing={car} onSave={save} onClose={() => setForm(null)} />}
      {form === "tank" && car && <TankCalcForm store={store} car={car} onSave={save} onClose={() => setForm(null)} />}
    </div>
  );
}

/** Consumption number formatting, matching mobile: ≥10 → 1 decimal, else 2. */
function fmtCons(v: number): string {
  return v >= 10 ? v.toFixed(1) : v.toFixed(2);
}
function inGarage(createdAt: number | null | undefined): string {
  if (createdAt == null) return "—";
  const days = Math.floor((Date.now() / 1000 - createdAt) / 86400);
  if (days <= 0) return "TODAY";
  if (days === 1) return "1 DAY";
  return `${days} DAYS`;
}

function CarBody({
  store,
  car,
  economy,
  currency,
  metric,
}: {
  store: CanonicalStore;
  car: Car;
  economy: ReturnType<typeof makeEconomy>;
  currency: string;
  metric: boolean;
}) {
  const logs = fuelLogsForCar(store.fuelLogs, car.id);
  const isEV = (car.fuelType ?? "").toLowerCase() === "electric";
  // Ordered by odometer, oldest→newest (mobile GarageStatsViewModel).
  const ord = [...logs].sort((a, b) => a.odometer - b.odometer);
  const maxOdo = ord.length ? ord[ord.length - 1]!.odometer : 0;
  const minOdo = ord.length ? ord[0]!.odometer : 0;

  // AVG CONSUMPTION = the latest fill's consumption (matches mobile: last valid
  // fuel/charge pair by odometer), NOT a fleet full-to-full average.
  let cons: number | null = null;
  if (isEV) {
    const s = ord.filter((l) => (l.kwhAdded ?? 0) > 0);
    if (s.length >= 2) cons = economy.consumptionValue(s[s.length - 1]!.odometer - s[s.length - 2]!.odometer, s[s.length - 1]!.kwhAdded ?? 0, true);
  } else {
    for (let i = ord.length - 1; i >= 1; i--) {
      const l = ord[i]!, p = ord[i - 1]!;
      if (l.volumeAdded > 0 && p.volumeAdded > 0) {
        cons = economy.consumptionValue(l.odometer - p.odometer, l.volumeAdded, false);
        if (cons != null) break;
      }
    }
  }

  // DISTANCE = current odometer − odometer at purchase (fall back to earliest fill).
  const baseline = (car.purchaseOdometer != null && car.purchaseOdometer > 0) ? car.purchaseOdometer : minOdo;
  const distDisplay = economy.convertDistance(Math.max(0, maxOdo - baseline));
  const odoDisplay = maxOdo > 0 ? economy.convertDistance(maxOdo) : 0;

  // COST / distance = fuel cost (every fill except the first) ÷ fuel odometer range.
  // Web amounts are already in the display currency (converted at import).
  const costDistKm = maxOdo - minOdo;
  const fuelCost = ord.slice(1).reduce((s, l) => s + l.totalCost, 0);
  const costPerKm = costDistKm > 0 ? fuelCost / economy.convertDistance(costDistKm) : null;

  const lastDate = logs.reduce<number | null>((m, l) => (l.date != null && (m == null || l.date > m) ? l.date : m), null);
  const daysSince = lastDate != null ? Math.max(0, Math.round((Date.now() / 1000 - lastDate) / 86400)) : null;
  const mfgRaw = isEV ? car.manufacturerAvgEnergy : car.manufacturerAvgConsumption;
  const mfgDisplay = mfgRaw == null || mfgRaw <= 0 ? "—"
    : (metric || isEV ? fmtCons(mfgRaw) : (235.214583 / mfgRaw).toFixed(1));
  const lastLabel = ((car.tankVolume ?? 0) > 0 && (car.batteryCapacity ?? 0) > 0) ? "Last Entry" : isEV ? "Last Charge" : "Last Fill";
  const battery = (car.batteryCapacity ?? 0) > 0 ? car.batteryCapacity! : null;

  return (
    <>
      <div className="panel" style={{ marginTop: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div className="eyebrow">CHASSIS №</div>
            <div className="mono" style={{ color: "var(--creamDim)", marginTop: 2 }}>
              {car.vin?.slice(-8) ?? car.licensePlate ?? car.id.slice(0, 8).toUpperCase()}
            </div>
          </div>
          <div className="amber" style={{ fontFamily: "var(--display)", fontWeight: 900, fontSize: 28 }}>{car.year || ""}</div>
        </div>
        <div style={{ fontFamily: "var(--display)", fontWeight: 900, fontSize: 34, marginTop: 10 }}>
          {(car.make ?? "").toUpperCase()}
        </div>
        <div className="amber" style={{ fontFamily: "var(--display)", fontWeight: 800, fontSize: 26 }}>{car.model ?? ""}</div>
        <div className="mono muted" style={{ fontSize: 11, letterSpacing: 1.4, marginTop: 8, display: "flex", gap: 16, flexWrap: "wrap" }}>
          <span>PLATE {car.licensePlate ?? "—"}</span>
          <span>COLOR {car.color ?? "—"}</span>
          <span>IN GARAGE {inGarage(car.createdAt)}</span>
        </div>
      </div>

      <div className="statgrid" style={{ marginTop: 12 }}>
        <StatTile k="Avg Consumption" value={cons != null ? fmtCons(cons) : "—"} unit={economy.economyLabel(isEV)} color="var(--cyan)" />
        <StatTile k="MFG Avg" value={mfgDisplay} unit={economy.economyLabel(isEV)} />
        <StatTile k={`Cost / ${economy.distanceLabel()}`} value={costPerKm != null ? fmtCons(costPerKm) : "—"} unit={`${currency}/${economy.distanceLabel()}`} color="var(--lime)" />
        <StatTile k="Distance" value={round0(distDisplay)} unit={economy.distanceLabel()} color="var(--cyan)" />
        <StatTile k={lastLabel} value={daysSince != null ? String(daysSince) : "—"} unit={daysSince != null ? "days ago" : ""} color="var(--amber)" />
        <StatTile k="Odometer" value={round0(odoDisplay)} unit={economy.distanceLabel()} />
        {battery != null && <StatTile k="Battery" value={round0(battery)} unit="kWh" color="var(--amber)" />}
        <StatTile k="Insurance" value={car.insuranceAmount ? round0(car.insuranceAmount) : "—"} unit={currency} color="var(--lime)" />
        <StatTile k="Road Tax" value={car.roadTaxAmount ? round0(car.roadTaxAmount) : "—"} unit={currency} color="var(--lime)" />
      </div>
    </>
  );
}
